import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Unified assessment editor drawer — the single UI for every entry path
 * (standalone Assessments screen, "create passing from lesson", edit an
 * existing assessment). Holds all mutable state locally; nothing hits the
 * server until Sam clicks "שמור".
 *
 * Level model (spec 2.3 / 6.2 / 6.3): a quiz path is an ordered list of
 * levels. Each level owns MULTIPLE template links — the engine picks one
 * at random per attempt without repetition. rescue_limit is per level.
 * Empty levels are UI ephemera; they're dropped before save (server
 * rejects them explicitly).
 *
 * Orphan avoidance: create mode does not POST until save; edit mode does
 * not PUT until save. Closing the drawer without saving leaves the server
 * untouched.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Drawer, Empty, Form, Input, InputNumber, Modal, Popconfirm, Popover, Select, Space, Spin, Tag, Tooltip, Typography, } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined, CloseOutlined, DeleteOutlined, EditOutlined, ExperimentOutlined, PlusOutlined, WarningOutlined, } from '@ant-design/icons';
import { createAssessment, getAssessment, getTemplate, listTemplates, putAssessmentStructure, updateAssessmentName, } from '../../api';
import { notifyError, notifySuccess } from '../errors';
import { TemplateWithRuleSetModal } from './TemplateWithRuleSetModal';
import { RuleSetModal } from '../content/RuleSetModal';
import { formatHebrew, parseExpression, variablesFromTemplate } from '../content/ruleBuilder';
import { ClipCascader, LessonCascader, useClipLabel, useLessonLabel } from '../entityPickers';
const { Text } = Typography;
// ------------------------------------------------------------
// Public component
// ------------------------------------------------------------
export function AssessmentEditorDrawer(props) {
    return (_jsx(Drawer, { open: props.open, onClose: props.onClose, width: 820, title: props.mode === 'create' ? 'בוחן חדש' : `עריכת בוחן #${props.assessmentId}`, extra: props.headerExtra, destroyOnClose: true, keyboard: false, maskClosable: false, children: props.mode === 'create'
            ? _jsx(EditorBody, { initial: initialDraftForCreate(props.lockKind, props.prefill), lockKind: props.lockKind, onSubmit: (draft) => saveCreate(draft), onSaved: props.onSaved, onClose: props.onClose })
            : _jsx(EditFromServer, { assessmentId: props.assessmentId, onSaved: props.onSaved, onClose: props.onClose }) }));
}
// ------------------------------------------------------------
// Page-mode entry point — renders EditFromServer directly (no Drawer).
// Used by /assessments/:id. Callers provide onSaved (usually a refetch) and
// onClose (usually a navigate back). The header actions (delete/unlink)
// live on the page shell, not on this body.
// ------------------------------------------------------------
export function AssessmentEditorPageBody(props) {
    return _jsx(EditFromServer, { ...props });
}
// ------------------------------------------------------------
// Edit-mode loader — fetches once, then hands off to EditorBody.
// ------------------------------------------------------------
function EditFromServer({ assessmentId, onSaved, onClose }) {
    const [detail, setDetail] = useState(null);
    const [err, setErr] = useState(null);
    useEffect(() => {
        (async () => {
            try {
                setDetail(await getAssessment(assessmentId));
            }
            catch (e) {
                setErr(e.message); /* client auto-surfaces toast; Alert renders detail */
            }
        })();
    }, [assessmentId]);
    if (err)
        return _jsx(Alert, { type: "error", message: err });
    if (!detail)
        return _jsx(Spin, {});
    return (_jsx(EditorBody, { initial: initialDraftForEdit(detail), 
        // Kind is fixed for existing assessments (changing kind changes the
        // linkage rules → simpler to force delete-and-recreate).
        lockKind: detail.kind, onSubmit: (draft) => saveEdit(assessmentId, detail, draft), onSaved: onSaved, onClose: onClose }));
}
// ------------------------------------------------------------
// Initial state builders
// ------------------------------------------------------------
function initialDraftForCreate(lockKind, prefill) {
    return {
        kind: lockKind ?? 'passing',
        name: prefill?.name ?? '',
        lesson_id: prefill?.lesson_id ?? null,
        clip_id: prefill?.clip_id ?? null,
        topic_label: prefill?.topic_label ?? '',
        levels: [],
    };
}
function initialDraftForEdit(detail) {
    return {
        kind: detail.kind,
        name: detail.name ?? '',
        lesson_id: detail.lesson_id,
        clip_id: detail.clip_id,
        topic_label: detail.topic_label ?? '',
        levels: detail.levels.map((lvl) => ({
            rescue_limit: lvl.rescue_limit,
            links: lvl.links.map((l) => ({
                question_template_id: l.question_template_id,
                rule_set_id: l.rule_set_id,
                rule_set_name: l.rule_set_name,
                rule_set_rules: l.rule_set_rules,
            })),
        })),
    };
}
// ------------------------------------------------------------
// Save routines — turn the draft into server calls. Only these two touch
// the network; every UI event above just mutates local state.
// ------------------------------------------------------------
function toStructurePayload(levels) {
    // Drop empty levels — the server rejects them, and they're a client-only
    // in-progress artifact. Sam sees a "add at least one template" hint on
    // any empty level in the UI; we simply refuse to send them.
    return levels
        .filter((l) => l.links.length > 0)
        .map((l) => ({
        rescue_limit: l.rescue_limit,
        links: l.links.map((k) => ({
            question_template_id: k.question_template_id,
            rule_set_id: k.rule_set_id,
        })),
    }));
}
async function saveCreate(draft) {
    const trimmedName = draft.name.trim();
    const linkage = {};
    if (draft.kind === 'passing')
        linkage.lesson_id = draft.lesson_id;
    if (draft.kind === 'practice')
        linkage.clip_id = draft.clip_id;
    if (draft.kind === 'blocking')
        linkage.topic_label = draft.topic_label.trim();
    const structure = toStructurePayload(draft.levels);
    const r = await createAssessment({
        kind: draft.kind,
        name: trimmedName || null,
        ...linkage,
        levels: structure.length > 0 ? structure : undefined,
    });
    return r.id;
}
async function saveEdit(id, before, draft) {
    const trimmedName = draft.name.trim();
    const beforeName = before.name ?? '';
    if (trimmedName !== beforeName) {
        await updateAssessmentName(id, trimmedName || null);
    }
    await putAssessmentStructure(id, toStructurePayload(draft.levels));
    return id;
}
// ------------------------------------------------------------
// The editor body — one form, one local `draft`, one save button.
// ------------------------------------------------------------
function EditorBody({ initial, lockKind, onSubmit, onSaved, onClose }) {
    const [draft, setDraft] = useState(initial);
    const [saving, setSaving] = useState(false);
    const [templates, setTemplates] = useState(null);
    // Dismissed cross-level dup warnings (per session). Keyed by
    // "template_id:rule_set_id" — dismissing hides the icon on every row for
    // that pair. Non-persisted: reopening the drawer starts fresh.
    const [dismissedDupes, setDismissedDupes] = useState(new Set());
    // In-place rule-set edit — launched from any LinkRow. We fetch the
    // owning template so the modal gets the full {name, is_default, rules}
    // shape it needs, plus the variables derived from the template's
    // prompt + answer_rule for the builder's dropdowns.
    const [editingRS, setEditingRS] = useState(null);
    useEffect(() => {
        // No .catch → central pipeline surfaces via setErrorReporter.
        // Adding a .catch(notifyError) here would double-toast.
        void listTemplates().then(setTemplates).catch(() => { });
    }, []);
    // Cross-level dup detection — a pair (template_id, rule_set_id) that lives
    // in more than one level is a soft warning: same difficulty pool duplicated.
    // Usually a mistake; occasionally intentional (same template with same
    // set at both L1 and L2 for review). Non-blocking, dismissible.
    const dupKeys = useMemo(() => {
        const counts = new Map();
        for (const level of draft.levels) {
            // Same pair inside a single level is already server-rejected as a
            // duplicate; only count it once per level for cross-level analysis.
            const seenInLevel = new Set();
            for (const link of level.links) {
                const key = `${link.question_template_id}:${link.rule_set_id}`;
                if (seenInLevel.has(key))
                    continue;
                seenInLevel.add(key);
                counts.set(key, (counts.get(key) ?? 0) + 1);
            }
        }
        const dupes = new Set();
        for (const [k, n] of counts)
            if (n > 1)
                dupes.add(k);
        return dupes;
    }, [draft.levels]);
    // Local mutators — kept as memoized callbacks so nested components don't
    // re-render every draft change.
    const setName = (v) => setDraft((d) => ({ ...d, name: v }));
    const setKind = (v) => setDraft((d) => ({
        ...d, kind: v,
        lesson_id: v === 'passing' ? d.lesson_id : null,
        clip_id: v === 'practice' ? d.clip_id : null,
        topic_label: v === 'blocking' ? d.topic_label : '',
    }));
    const setLessonId = (v) => setDraft((d) => ({ ...d, lesson_id: v }));
    const setClipId = (v) => setDraft((d) => ({ ...d, clip_id: v }));
    const setTopic = (v) => setDraft((d) => ({ ...d, topic_label: v }));
    const addLevel = () => setDraft((d) => ({ ...d, levels: [...d.levels, { rescue_limit: null, links: [] }] }));
    const removeLevel = (idx) => setDraft((d) => ({ ...d, levels: d.levels.filter((_, i) => i !== idx) }));
    const moveLevel = (idx, dir) => setDraft((d) => {
        const j = idx + dir;
        if (j < 0 || j >= d.levels.length)
            return d;
        const next = [...d.levels];
        [next[idx], next[j]] = [next[j], next[idx]];
        return { ...d, levels: next };
    });
    const setRescue = (idx, v) => setDraft((d) => ({ ...d, levels: d.levels.map((l, i) => i === idx ? { ...l, rescue_limit: v } : l) }));
    const addLinkToLevel = (idx, link) => {
        // Client-side dedup — the server rejects duplicates on save (409
        // duplicate from validateLevels), but that's too late: the editor
        // holds all state locally until save, so a silent no-op here would
        // leave Sam thinking his click did nothing until the server round-trip
        // hours later. Surface the same message the server would, immediately.
        const level = draft.levels[idx];
        const exists = level.links.some((k) => k.question_template_id === link.question_template_id &&
            k.rule_set_id === link.rule_set_id);
        if (exists) {
            notifyError({ message: 'אותה תבנית עם אותו סט חוקים כבר קיימת ברמה זו' });
            return;
        }
        setDraft((d) => ({
            ...d,
            levels: d.levels.map((l, i) => i === idx ? { ...l, links: [...l.links, link] } : l),
        }));
    };
    const removeLink = (levelIdx, linkIdx) => setDraft((d) => ({
        ...d,
        levels: d.levels.map((l, i) => i === levelIdx ? { ...l, links: l.links.filter((_, j) => j !== linkIdx) } : l),
    }));
    /**
     * Open the shared RuleSetModal pre-filled with the current state of
     * the rule set belonging to a LinkRow. We fetch the owning template
     * on demand because the enriched link payload only carries the set's
     * display name + rule expressions, not the per-rule ids / default
     * flag / description that the modal needs for round-tripping.
     */
    const openEditRuleSet = async (templateId, ruleSetId) => {
        try {
            const t = await getTemplate(templateId);
            const rs = t.rule_sets.find((x) => x.id === ruleSetId);
            if (!rs) {
                notifyError({ message: 'הסט לא נמצא' });
                return;
            }
            setEditingRS({
                templateId,
                ruleSet: rs,
                variables: variablesFromTemplate(t.prompt, t.answer_rule),
            });
        }
        catch { /* client auto-surfaces */ }
    };
    /**
     * After the modal saves, patch every draft link that references the
     * edited rule set so its tag + rules tooltip reflect the new state
     * without wiping the user's unsaved draft changes elsewhere. One
     * extra template fetch; cheap.
     */
    const onRuleSetSaved = async (templateId, ruleSetId) => {
        setEditingRS(null);
        try {
            const t = await getTemplate(templateId);
            const rs = t.rule_sets.find((x) => x.id === ruleSetId);
            if (!rs)
                return;
            setDraft((d) => ({
                ...d,
                levels: d.levels.map((lvl) => ({
                    ...lvl,
                    links: lvl.links.map((link) => link.rule_set_id === ruleSetId
                        ? {
                            ...link,
                            rule_set_name: rs.name,
                            rule_set_rules: rs.rules.map((r) => r.rule_expression),
                        }
                        : link),
                })),
            }));
        }
        catch { /* client auto-surfaces */ }
    };
    const linkageValid = draft.kind === 'passing' ? draft.lesson_id != null :
        draft.kind === 'practice' ? draft.clip_id != null :
            draft.topic_label.trim() !== '';
    const anyEmptyLevel = draft.levels.some((l) => l.links.length === 0);
    const submit = useCallback(async () => {
        if (!linkageValid) {
            notifyError({ message: 'הצמדה חסרה — יש למלא את שדה הקישור לסוג' });
            return;
        }
        if (anyEmptyLevel) {
            notifyError({ message: 'יש רמות ריקות — הוסף לפחות תבנית אחת לכל רמה, או הסר את הרמה' });
            return;
        }
        setSaving(true);
        try {
            const id = await onSubmit(draft);
            notifySuccess('הבוחן נשמר');
            onSaved(id);
        }
        catch { /* client auto-surfaces */ }
        finally {
            setSaving(false);
        }
    }, [draft, linkageValid, anyEmptyLevel, onSubmit, onSaved]);
    return (_jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: 16 }, children: [_jsx(TopScalars, { draft: draft, lockKind: lockKind, onName: setName, onKind: setKind, onLesson: setLessonId, onClip: setClipId, onTopic: setTopic }), _jsxs("div", { children: [_jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }, children: [_jsx(Typography.Title, { level: 5, style: { margin: 0 }, children: "\u05E8\u05DE\u05D5\u05EA \u05D4\u05D1\u05D5\u05D7\u05DF" }), _jsx(Button, { size: "small", icon: _jsx(PlusOutlined, {}), onClick: addLevel, children: "\u05D4\u05D5\u05E1\u05E3 \u05E8\u05DE\u05D4" }), _jsx(Text, { type: "secondary", style: { marginInlineStart: 8 }, children: "\u05DB\u05DC \u05E8\u05DE\u05D4 \u05DE\u05DB\u05D9\u05DC\u05D4 \u05D0\u05D5\u05E1\u05E3 \u05EA\u05D1\u05E0\u05D9\u05D5\u05EA. \u05D4\u05DE\u05E2\u05E8\u05DB\u05EA \u05D1\u05D5\u05D7\u05E8\u05EA \u05D0\u05D7\u05EA \u05DE\u05D4\u05DF \u05D1\u05DB\u05DC \u05E0\u05D9\u05E1\u05D9\u05D5\u05DF, \u05D1\u05DC\u05D9 \u05DC\u05D7\u05D6\u05D5\u05E8 \u05E2\u05DC \u05D0\u05D5\u05EA\u05D4 \u05EA\u05D1\u05E0\u05D9\u05EA \u05D1\u05E8\u05E6\u05E3." })] }), draft.levels.length === 0
                        ? _jsx(Empty, { description: "\u05D0\u05D9\u05DF \u05E2\u05D3\u05D9\u05D9\u05DF \u05E8\u05DE\u05D5\u05EA \u2014 \u05D4\u05D5\u05E1\u05E3 \u05E8\u05DE\u05D4 \u05E8\u05D0\u05E9\u05D5\u05E0\u05D4", image: Empty.PRESENTED_IMAGE_SIMPLE })
                        : (_jsx(Space, { direction: "vertical", size: 12, style: { width: '100%' }, children: draft.levels.map((lvl, i) => (_jsx(LevelCard, { index: i, isFirst: i === 0, isLast: i === draft.levels.length - 1, level: lvl, templates: templates, dupKeys: dupKeys, dismissedDupes: dismissedDupes, onDismissDup: (key) => setDismissedDupes((prev) => {
                                    const next = new Set(prev);
                                    next.add(key);
                                    return next;
                                }), onMoveUp: () => moveLevel(i, -1), onMoveDown: () => moveLevel(i, 1), onRemove: () => removeLevel(i), onSetRescue: (v) => setRescue(i, v), onAddLink: (link) => addLinkToLevel(i, link), onRemoveLink: (j) => removeLink(i, j), onEditRuleSet: openEditRuleSet }, i))) }))] }), _jsxs(Space, { style: { marginTop: 8, justifyContent: 'flex-end', display: 'flex' }, children: [_jsx(Button, { onClick: onClose, children: "\u05D1\u05D8\u05DC" }), _jsx(Button, { type: "primary", loading: saving, disabled: !linkageValid, onClick: submit, children: "\u05E9\u05DE\u05D5\u05E8" })] }), editingRS && (_jsx(RuleSetModal, { open: true, onClose: () => setEditingRS(null), onDone: () => onRuleSetSaved(editingRS.templateId, editingRS.ruleSet.id), templateId: editingRS.templateId, variables: editingRS.variables, initial: editingRS.ruleSet }))] }));
}
// ------------------------------------------------------------
// Top scalars — name + kind + linkage (locked in edit mode / lesson flow).
// ------------------------------------------------------------
function TopScalars({ draft, lockKind, onName, onKind, onLesson, onClip, onTopic }) {
    const kindOptions = [
        { value: 'passing', label: 'מעבר — נתלה בשיעור' },
        { value: 'practice', label: 'תרגול — נתלה בקליפ' },
        { value: 'blocking', label: 'חוסם — נתלה ב-topic' },
    ];
    return (_jsx(Card, { size: "small", title: "\u05E4\u05E8\u05D8\u05D9 \u05D1\u05D5\u05D7\u05DF", children: _jsxs(Form, { layout: "vertical", children: [_jsx(Form.Item, { label: "\u05E9\u05DD", required: true, children: _jsx(Input, { value: draft.name, placeholder: "\u05E9\u05DD \u05D9\u05D3\u05D9\u05D3\u05D5\u05EA\u05D9 (\u05DC\u05DE\u05E9\u05DC: '\u05DE\u05E9\u05D5\u05D5\u05D0\u05D5\u05EA \u05D1\u05E1\u05D9\u05E1\u05D9\u05D5\u05EA \u2014 \u05DE\u05E2\u05D1\u05E8')", onChange: (e) => onName(e.target.value) }) }), _jsx(Form.Item, { label: "\u05E1\u05D5\u05D2 \u05D1\u05D5\u05D7\u05DF", children: _jsx(Select, { value: draft.kind, options: kindOptions, onChange: (v) => onKind(v), disabled: !!lockKind }) }), draft.kind === 'passing' && (_jsx(Form.Item, { label: "\u05E9\u05D9\u05E2\u05D5\u05E8", required: true, children: _jsx(LessonCascader, { value: draft.lesson_id, onChange: onLesson, disabled: !!lockKind }) })), draft.kind === 'practice' && (_jsx(Form.Item, { label: "\u05E7\u05DC\u05D9\u05E4", required: true, children: _jsx(ClipCascader, { value: draft.clip_id, onChange: onClip, disabled: !!lockKind }) })), draft.kind === 'blocking' && (_jsx(Form.Item, { label: "topic label", required: true, children: _jsx(Input, { value: draft.topic_label, placeholder: "\u05DC\u05DE\u05E9\u05DC: \u05E4\u05D9\u05EA\u05D2\u05D5\u05E8\u05E1", onChange: (e) => onTopic(e.target.value) }) }))] }) }));
}
// ------------------------------------------------------------
// LevelCard — one level's UI: header (# + rescue + reorder + remove) +
// list of template links + "add template" affordances.
// ------------------------------------------------------------
function LevelCard({ index, isFirst, isLast, level, templates, dupKeys, dismissedDupes, onDismissDup, onMoveUp, onMoveDown, onRemove, onSetRescue, onAddLink, onRemoveLink, onEditRuleSet, }) {
    const [pickerOpen, setPickerOpen] = useState(false);
    const [newTemplateOpen, setNewTemplateOpen] = useState(false);
    const removeAction = (level.links.length === 0
        ? _jsx(Button, { size: "small", danger: true, icon: _jsx(CloseOutlined, {}), onClick: onRemove, children: "\u05D4\u05E1\u05E8 \u05E8\u05DE\u05D4" })
        : (_jsx(Popconfirm, { title: "\u05DC\u05D4\u05E1\u05D9\u05E8 \u05E8\u05DE\u05D4?", description: `ברמה זו ${level.links.length} תבניות מוצמדות. הסרה תמחק את ההצמדות ואת מספר סבבי ההצלה שהוגדרו לרמה זו (בשמירה).`, okText: "\u05D4\u05E1\u05E8", cancelText: "\u05D1\u05D8\u05DC", okButtonProps: { danger: true }, onConfirm: onRemove, children: _jsx(Button, { size: "small", danger: true, icon: _jsx(CloseOutlined, {}), children: "\u05D4\u05E1\u05E8 \u05E8\u05DE\u05D4" }) })));
    return (_jsxs(Card, { size: "small", title: _jsxs(Space, { wrap: true, children: [_jsxs(Tag, { color: "blue", children: ["\u05E8\u05DE\u05D4 ", index + 1] }), _jsx(Tooltip, { title: "\u05DE\u05E1\u05E4\u05E8 \u05D4\u05E4\u05E2\u05DE\u05D9\u05DD \u05E9\u05D4\u05DE\u05E2\u05E8\u05DB\u05EA \u05EA\u05D9\u05EA\u05DF \u05DC\u05EA\u05DC\u05DE\u05D9\u05D3 \u05D6\u05D5\u05D2 \u05E9\u05D0\u05DC\u05D5\u05EA \u05D4\u05E6\u05DC\u05D4 \u05D1\u05E8\u05DE\u05D4 \u05D4\u05D6\u05D0\u05EA \u05DC\u05D0\u05D7\u05E8 \u05EA\u05E9\u05D5\u05D1\u05D4 \u05E9\u05D2\u05D5\u05D9\u05D4. \u05D0\u05DD \u05E0\u05D9\u05E6\u05DC \u05D0\u05EA \u05DB\u05DC \u05D4\u05E1\u05D1\u05D1\u05D9\u05DD \u05D5\u05DC\u05D0 \u05D4\u05E6\u05DC\u05D9\u05D7, \u05D4\u05D1\u05D5\u05D7\u05DF \u05DE\u05E1\u05EA\u05D9\u05D9\u05DD \u05DB\u05DB\u05D9\u05E9\u05DC\u05D5\u05DF. \u05D1\u05E8\u05D9\u05E8\u05EA \u05DE\u05D7\u05D3\u05DC: 2.", children: _jsx("span", { style: { cursor: 'help' }, children: "\u05E1\u05D1\u05D1\u05D9 \u05D4\u05E6\u05DC\u05D4:" }) }), _jsx(InputNumber, { size: "small", min: 0, value: level.rescue_limit ?? undefined, placeholder: "2", onChange: (v) => onSetRescue(v == null ? null : Number(v)) })] }), extra: _jsxs(Space, { children: [_jsx(Tooltip, { title: "\u05D4\u05D6\u05D6 \u05DC\u05DE\u05E2\u05DC\u05D4", children: _jsx(Button, { size: "small", icon: _jsx(ArrowUpOutlined, {}), disabled: isFirst, onClick: onMoveUp }) }), _jsx(Tooltip, { title: "\u05D4\u05D6\u05D6 \u05DC\u05DE\u05D8\u05D4", children: _jsx(Button, { size: "small", icon: _jsx(ArrowDownOutlined, {}), disabled: isLast, onClick: onMoveDown }) }), removeAction] }), style: level.links.length === 0 ? { borderColor: '#faad14' } : undefined, children: [level.links.length === 0
                ? _jsx(Alert, { type: "warning", message: "\u05E8\u05DE\u05D4 \u05E8\u05D9\u05E7\u05D4 \u2014 \u05D4\u05D5\u05E1\u05E3 \u05DC\u05E4\u05D7\u05D5\u05EA \u05EA\u05D1\u05E0\u05D9\u05EA \u05D0\u05D7\u05EA \u05DC\u05E4\u05E0\u05D9 \u05E9\u05DE\u05D9\u05E8\u05D4", style: { marginBottom: 12 } })
                : (_jsx(Space, { direction: "vertical", size: 4, style: { width: '100%', marginBottom: 12 }, children: level.links.map((k, j) => {
                        const key = `${k.question_template_id}:${k.rule_set_id}`;
                        const isDup = dupKeys.has(key) && !dismissedDupes.has(key);
                        return (_jsx(LinkRow, { link: k, templates: templates, dupWarning: isDup, onDismissDup: () => onDismissDup(key), onRemove: () => onRemoveLink(j), onEditRuleSet: () => onEditRuleSet(k.question_template_id, k.rule_set_id) }, j));
                    }) })), _jsxs(Space, { wrap: true, children: [_jsx(Button, { icon: _jsx(PlusOutlined, {}), onClick: () => setPickerOpen(true), children: "\u05D4\u05E6\u05DE\u05D3 \u05EA\u05D1\u05E0\u05D9\u05EA \u05E7\u05D9\u05D9\u05DE\u05EA" }), _jsx(Button, { icon: _jsx(ExperimentOutlined, {}), onClick: () => setNewTemplateOpen(true), children: "\u05E6\u05D5\u05E8 \u05EA\u05D1\u05E0\u05D9\u05EA \u05D7\u05D3\u05E9\u05D4" })] }), pickerOpen && templates && (_jsx(TemplateAndRuleSetPickerModal, { templates: templates, onClose: () => setPickerOpen(false), onPick: (link) => { setPickerOpen(false); onAddLink(link); } })), _jsx(TemplateWithRuleSetModal, { open: newTemplateOpen, onClose: () => setNewTemplateOpen(false), onCreated: (pair) => {
                    setNewTemplateOpen(false);
                    onAddLink({ question_template_id: pair.template_id, rule_set_id: pair.rule_set_id });
                } })] }));
}
// ------------------------------------------------------------
// One row in a level — the linked template+rule_set, with a remove button.
// Names are resolved from the fetched templates list (or shown as ids if
// the templates list is still loading).
// ------------------------------------------------------------
function LinkRow({ link, templates, dupWarning, onDismissDup, onRemove, onEditRuleSet }) {
    const t = templates?.find((x) => x.id === link.question_template_id);
    // Primary content: the question prompt. Secondary content: the id,
    // kept small so non-technical users don't lead with it.
    const prompt = t?.prompt ?? null;
    const promptPreview = prompt
        ? (prompt.length > 70 ? `${prompt.slice(0, 70)}…` : prompt)
        : null;
    // Rule set tag: show the name if we have it; fall back to the id. The
    // tooltip lists the rules in human-readable Hebrew via the shared
    // formatter. Expressions the formatter can't phrase (advanced) fall
    // back to the raw expression as monospace secondary text.
    const ruleSetTagLabel = link.rule_set_name ?? `סט ${link.rule_set_id}`;
    const ruleSetRules = link.rule_set_rules ?? [];
    const ruleSetTooltip = ruleSetRules.length === 0
        ? 'אין חוקים בסט'
        : (_jsxs("div", { style: { maxWidth: 320 }, children: [_jsx("div", { style: { marginBottom: 4, fontWeight: 500 }, children: "\u05D7\u05D5\u05E7\u05D9 \u05D4\u05E1\u05D8" }), ruleSetRules.map((expr, i) => {
                    const human = formatHebrew(parseExpression(expr));
                    return (_jsxs("div", { style: { marginTop: 2 }, children: [human
                                ? _jsx("span", { children: human })
                                : _jsx("code", { style: { fontSize: 11 }, children: expr }), i < ruleSetRules.length - 1 && (_jsx("span", { style: { color: '#8B2FD6', fontWeight: 500 }, children: " \u05D5\u05D2\u05DD " }))] }, i));
                })] }));
    return (_jsxs("div", { style: {
            display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px',
            background: dupWarning ? '#fffbe6' : '#fafafa',
            borderRadius: 4,
            border: dupWarning ? '1px solid #ffe58f' : '1px solid transparent',
        }, children: [dupWarning && (_jsx(Popover, { trigger: ['hover', 'click'], placement: "left", content: _jsxs("div", { style: { maxWidth: 320 }, children: [_jsx("div", { style: { marginBottom: 8 }, children: "\u05D0\u05D5\u05EA\u05D4 \u05EA\u05D1\u05E0\u05D9\u05EA \u05E2\u05DD \u05D0\u05D5\u05EA\u05D5 \u05E1\u05D8 \u05D7\u05D5\u05E7\u05D9\u05DD \u05D1\u05E9\u05EA\u05D9 \u05E8\u05DE\u05D5\u05EA, \u05D9\u05D9\u05EA\u05DB\u05DF \u05E9\u05D4\u05EA\u05DB\u05D5\u05D5\u05E0\u05EA \u05DC\u05E1\u05D8 \u05E9\u05D5\u05E0\u05D4?" }), _jsx(Button, { size: "small", onClick: onDismissDup, children: "\u05D1\u05D8\u05DC \u05D0\u05D6\u05D4\u05E8\u05D4" })] }), children: _jsx(WarningOutlined, { style: { color: '#faad14', cursor: 'help' } }) })), _jsxs("div", { style: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsx(Text, { style: { fontWeight: 500 }, ellipsis: { tooltip: prompt ?? undefined }, children: promptPreview ?? `תבנית ${link.question_template_id}` }), _jsxs(Text, { type: "secondary", style: { fontSize: 11 }, children: ["\u05EA\u05D1\u05E0\u05D9\u05EA ", link.question_template_id] })] }), _jsx(Tooltip, { title: ruleSetTooltip, placement: "left", children: _jsx(Tag, { color: "purple", style: { cursor: 'help' }, children: ruleSetTagLabel }) }), _jsx(Tooltip, { title: link.rule_set_name === null ? 'הסט לא נמצא' : 'ערוך סט חוקים', children: _jsx(Button, { size: "small", icon: _jsx(EditOutlined, {}), disabled: link.rule_set_name === null, onClick: onEditRuleSet }) }), _jsxs(Text, { type: "secondary", style: { fontSize: 11 }, children: ["\u05E1\u05D8 ", link.rule_set_id] }), _jsx(Button, { size: "small", danger: true, icon: _jsx(DeleteOutlined, {}), onClick: onRemove })] }));
}
// ------------------------------------------------------------
// "Attach existing template" picker — pick a template, then a rule set
// belonging to it. Same two-step flow as the old AddLinkModal but now
// feeds a level rather than the assessment root.
// ------------------------------------------------------------
function TemplateAndRuleSetPickerModal({ templates, onClose, onPick }) {
    const [templateId, setTemplateId] = useState();
    // Keep the full rule-set objects (with rules) so we can show a preview.
    // Choosing the set IS choosing the difficulty — the rules make that
    // choice concrete, so they need to be visible, not buried behind an id.
    const [ruleSets, setRuleSets] = useState([]);
    const [ruleSetId, setRuleSetId] = useState();
    const [loading, setLoading] = useState(false);
    const [createSetOpen, setCreateSetOpen] = useState(false);
    // Trigger a rule-set refetch after we create one inline — the picker then
    // reveals the new option and we auto-select it.
    const [reloadKey, setReloadKey] = useState(0);
    const [pendingSelectId, setPendingSelectId] = useState(null);
    useEffect(() => {
        if (templateId == null) {
            setRuleSets([]);
            setRuleSetId(undefined);
            return;
        }
        setLoading(true);
        void getTemplate(templateId)
            .then((t) => {
            setRuleSets(t.rule_sets);
            // After creating a set inline the caller stashes its id in
            // pendingSelectId; on the fresh fetch we select that one. Otherwise
            // fall back to the template's default set.
            if (pendingSelectId != null && t.rule_sets.some((rs) => rs.id === pendingSelectId)) {
                setRuleSetId(pendingSelectId);
                setPendingSelectId(null);
            }
            else if (ruleSetId == null || !t.rule_sets.some((rs) => rs.id === ruleSetId)) {
                const def = t.rule_sets.find((rs) => rs.is_default) ?? t.rule_sets[0];
                if (def)
                    setRuleSetId(def.id);
            }
        })
            .catch(() => { })
            .finally(() => setLoading(false));
        // ruleSetId intentionally not in deps — we react only to template changes
        // and explicit reload triggers.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateId, reloadKey]);
    const submit = () => {
        if (templateId == null || ruleSetId == null)
            return;
        // Enrich with the chosen set's name + rules so the editor can
        // render the human-readable tag + tooltip without re-fetching.
        const rs = ruleSets.find((x) => x.id === ruleSetId);
        onPick({
            question_template_id: templateId,
            rule_set_id: ruleSetId,
            rule_set_name: rs?.name ?? null,
            rule_set_rules: rs ? rs.rules.map((r) => r.rule_expression) : [],
        });
    };
    const selectedRuleSet = ruleSets.find((rs) => rs.id === ruleSetId);
    return (_jsxs(Modal, { title: "\u05D4\u05E6\u05DE\u05D3 \u05EA\u05D1\u05E0\u05D9\u05EA \u05E7\u05D9\u05D9\u05DE\u05EA", open: true, onCancel: onClose, onOk: submit, okText: "\u05D4\u05E6\u05DE\u05D3", cancelText: "\u05D1\u05D8\u05DC", okButtonProps: { disabled: templateId == null || ruleSetId == null }, destroyOnHidden: true, width: 560, children: [_jsxs(Form, { layout: "vertical", children: [_jsx(Form.Item, { label: "\u05EA\u05D1\u05E0\u05D9\u05EA", required: true, children: _jsx(Select, { showSearch: true, optionFilterProp: "label", value: templateId, onChange: (v) => { setTemplateId(Number(v)); setRuleSetId(undefined); }, options: templates.map((t) => ({ value: t.id, label: `#${t.id}: ${t.prompt.slice(0, 40)}` })) }) }), _jsx(Form.Item, { label: _jsxs(Space, { size: 4, children: [_jsx("span", { children: "\u05E1\u05D8 \u05D7\u05D5\u05E7\u05D9\u05DD" }), _jsx(Text, { type: "secondary", style: { fontWeight: 'normal', fontSize: 12 }, children: "\u2014 \u05D1\u05D7\u05D9\u05E8\u05EA \u05D4\u05E1\u05D8 = \u05D1\u05D7\u05D9\u05E8\u05EA \u05D4\u05E7\u05D5\u05E9\u05D9" })] }), required: true, children: _jsx(Select, { value: ruleSetId, onChange: (v) => setRuleSetId(Number(v)), disabled: templateId == null, loading: loading, options: ruleSets.map((rs) => ({
                                value: rs.id,
                                label: rs.name + (rs.is_default ? ' (ברירת מחדל)' : ''),
                                rs,
                            })), optionRender: (opt) => {
                                const rs = opt.data.rs;
                                return _jsx(RuleSetOptionLabel, { ruleSet: rs });
                            }, 
                            // Sticky footer inside the dropdown — the "create new set for
                            // this template" affordance sits below the options so Sam
                            // discovers it in the same place they'd already be looking.
                            dropdownRender: (menu) => (_jsxs(_Fragment, { children: [menu, _jsx("div", { style: { padding: 4, borderTop: '1px solid #f0f0f0' }, children: _jsx(Button, { type: "text", block: true, icon: _jsx(PlusOutlined, {}), disabled: templateId == null, onClick: () => setCreateSetOpen(true), children: "\u05E6\u05D5\u05E8 \u05E1\u05D8 \u05D7\u05D3\u05E9 \u05DC\u05EA\u05D1\u05E0\u05D9\u05EA \u05D6\u05D5" }) })] })) }) }), selectedRuleSet && (_jsx(RuleSetRulesPreview, { ruleSet: selectedRuleSet }))] }), templateId != null && (() => {
                // Resolve the selected template's prompt + answer_rule from the
                // picker's templates list so the builder's variable dropdowns
                // show the right options.
                const t = templates.find((x) => x.id === templateId);
                const vars = t ? variablesFromTemplate(t.prompt, t.answer_rule) : [];
                return (_jsx(RuleSetModal, { open: createSetOpen, onClose: () => setCreateSetOpen(false), templateId: templateId, variables: vars, onDone: (newRuleSetId) => {
                        // Close the sub-modal, remember the new id, and bump the reload
                        // trigger — the useEffect above will refetch the rule sets and
                        // auto-select the new one, leaving the picker state (template
                        // choice) intact.
                        setCreateSetOpen(false);
                        setPendingSelectId(newRuleSetId);
                        setReloadKey((k) => k + 1);
                    } }));
            })()] }));
}
/**
 * How each rule-set option renders inside the Select dropdown. Shows the set
 * name + default badge + rule count in the row; a Popover with the full
 * rule expressions opens on hover so multi-set comparison is one glance
 * away instead of one save-and-check away.
 */
function RuleSetOptionLabel({ ruleSet }) {
    return (_jsx(Popover, { trigger: "hover", placement: "left", title: ruleSet.name, content: _jsx("div", { style: { maxWidth: 320 }, children: ruleSet.rules.length === 0
                ? _jsx(Text, { type: "secondary", children: "\u2014 \u05D0\u05D9\u05DF \u05D7\u05D5\u05E7\u05D9\u05DD \u2014" })
                : ruleSet.rules.map((r, i) => (_jsx("div", { children: _jsx(Text, { code: true, children: r.rule_expression }) }, i))) }), children: _jsxs("div", { style: { display: 'flex', alignItems: 'center', gap: 8, width: '100%' }, children: [_jsx("span", { style: { flex: 1 }, children: ruleSet.name }), ruleSet.is_default && _jsx(Tag, { color: "green", children: "\u05D1\u05E8\u05D9\u05E8\u05EA \u05DE\u05D7\u05D3\u05DC" }), _jsxs(Text, { type: "secondary", style: { fontSize: 12 }, children: [ruleSet.rules.length, " \u05D7\u05D5\u05E7\u05D9\u05DD"] })] }) }));
}
/**
 * Inline preview of the selected set's rules — always-on, right under the
 * picker. So the chosen "difficulty" is visible without hover, without a
 * separate dry-run trip. Truncated with an "עוד" if there are many rules.
 */
function RuleSetRulesPreview({ ruleSet }) {
    const MAX = 5;
    const shown = ruleSet.rules.slice(0, MAX);
    const rest = Math.max(0, ruleSet.rules.length - MAX);
    return (_jsx(Card, { size: "small", style: { background: '#fafafa' }, children: _jsxs(Space, { direction: "vertical", size: 2, style: { width: '100%' }, children: [_jsxs(Text, { strong: true, children: ["\u05D7\u05D5\u05E7\u05D9 \u05D4\u05E1\u05D8 \"", ruleSet.name, "\""] }), shown.length === 0
                    ? _jsx(Text, { type: "secondary", children: "\u2014 \u05D0\u05D9\u05DF \u05D7\u05D5\u05E7\u05D9\u05DD \u2014" })
                    : shown.map((r, i) => _jsx("div", { children: _jsx(Text, { code: true, children: r.rule_expression }) }, i)), rest > 0 && _jsxs(Text, { type: "secondary", children: ["\u05D5\u05E2\u05D5\u05D3 ", rest, " \u05D7\u05D5\u05E7\u05D9\u05DD\u2026"] })] }) }));
}
// ------------------------------------------------------------
// Small "summary" component consumers can render outside the drawer to
// show the resolved linkage — reused by both the standalone list and
// the lesson-context drawer's own preamble.
// ------------------------------------------------------------
export function AssessmentLinkageSummary({ assessment }) {
    const lessonLabel = useLessonLabel(assessment.lesson_id);
    const clipLabel = useClipLabel(assessment.clip_id);
    if (assessment.kind === 'passing') {
        return _jsx(Text, { type: "secondary", children: lessonLabel ?? (assessment.lesson_id != null ? `שיעור #${assessment.lesson_id}` : 'לא משויך') });
    }
    if (assessment.kind === 'practice') {
        return _jsx(Text, { type: "secondary", children: clipLabel ?? (assessment.clip_id != null ? `קליפ #${assessment.clip_id}` : 'לא משויך') });
    }
    return _jsx(Text, { type: "secondary", children: assessment.topic_label ?? 'לא משויך' });
}
